<script setup lang="ts">
import DeleteButton from "@/components/DeleteButton.vue";
import { useEditing } from "@/composables/useEditing";
import type { Conversation } from "@/conversations/types";
import { useConversationPermissions } from "@/conversations/useConversationPermissions";
import type { User } from "@/users/types";
import { formatDatetime } from "@/utils/toFormattedDatetime";
import { EditPencil, Heart, Trash } from "@iconoir/vue";
import type { MutationStatus } from "@tanstack/vue-query";
import { useTemplateRef } from "vue";
import { RouterLink } from "vue-router";

const {
  conversation,
  deletingStatus,
  me = undefined,
} = defineProps<{
  conversation: Conversation;
  deletingStatus: MutationStatus;
  me?: User;
}>();

const emit = defineEmits<{
  initiateDelete: [];
  delete: [conversation: Conversation];
  update: [conversation: Conversation];
}>();

const { canEdit, canDelete } = useConversationPermissions(
  () => me,
  () => conversation,
);

const { isEditing, draft, startEditing, cancelEditing, saveEditing } =
  useEditing(
    () => conversation.content,
    (content: string) => emit("update", { ...conversation, content }),
    useTemplateRef("editTextarea"),
  );
</script>

<template>
  <article>
    <header class="row">
      <small>
        {{ formatDatetime(conversation.createdAt) }}
        | {{ conversation.createdByUserName ?? "n/a" }}
      </small>
      <div v-if="!isEditing" class="row-self-space-auto row">
        <button
          v-if="canEdit"
          class="button button--tertiary"
          @click.prevent="startEditing"
        >
          <EditPencil /><span class="visually-hidden">bearbeiten</span>
        </button>
        <DeleteButton
          v-if="canDelete"
          class="button button--tertiary"
          :deleting-status="deletingStatus"
          @initiated="$emit('initiateDelete')"
          @confirmed="$emit('delete', conversation)"
        >
          <Trash /><span class="visually-hidden">entfernen</span>
          <template #confirmation>
            Möchtest du dieses Gespräch von
            {{ conversation.createdByUserName ?? "n/a" }} wirklich löschen?
          </template>
        </DeleteButton>
      </div>
    </header>
    <div v-if="!isEditing" class="card column">
      <p>{{ conversation.content }}</p>
      <p>
        <Heart />
        <RouterLink
          :to="{
            name: 'editCanvass',
            params: { id: conversation.canvassId },
          }"
          >{{ conversation.canvassName }}</RouterLink
        >
      </p>
    </div>
    <form v-else class="column" @submit.prevent="saveEditing">
      <label
        :for="`edit-conversation-${conversation.id}`"
        class="visually-hidden"
        >Gespräch bearbeiten</label
      >
      <textarea
        :id="`edit-conversation-${conversation.id}`"
        ref="editTextarea"
        v-model="draft"
        rows="10"
      ></textarea>
      <div class="row row-end">
        <button
          class="button button--secondary"
          type="reset"
          @click="cancelEditing"
        >
          Abbrechen
        </button>
        <button class="button" type="submit">Speichern</button>
      </div>
    </form>
  </article>
</template>
